import { RECOMMENDATION_TYPE } from '@/types';
import { AUDIENCE_CSV_IMPORT_TYPE } from './audiences';

export const IOT_GPT_API = {
  chatQuery: {
    api: '/private/chats/process-query',
  },
  chatQueryDev: {
    api: '/private/chats/process-query-dev',
  },
  getUserThreads: {
    api: '/private/chats/get-user-threads',
  },
  getThreadDetails: {
    api: (threadId: string) => `/private/chats/get-thread-details/${threadId}`,
  },
  getRecommendationProductDetail: {
    api: (query?: string | number, recommendationType?: string) =>
      recommendationType === RECOMMENDATION_TYPE.DEVICES
        ? `/public/devices/${query}`
        : `/public/products/${query}`,
  },
  deleteThread: {
    api: (threadId: string) => `/private/chats/thread/${threadId}`,
  },
  greetingsFeedback: {
    api: '/public/chatbot/feedback',
  },
  publicChatQueryDev: {
    api: '/public/chats/process-query-dev',
  },
};

export const PRODUCT_API = {
  queryDevice: {
    api: '/public/chats/queryDevice',
  },
  getVerifiedAlternateProducts: {
    api: (productId: number) => `/public/products/${productId}/verified-alternate-products`,
  },
};

export const PROJECTS_API = {
  getAllProjectsByUser: {
    api: '/private/projects/',
  },
  createNewProject: {
    api: '/private/projects/',
  },
  addProductToProject: {
    api: (projectId: string | number) => `/private/projects/${projectId}/products`,
  },
  checkProductSaved: {
    api: (productId: string | number) => `/private/projects/check-product/${productId}`,
  },
  getAllSavedProducts: {
    api: '/private/projects/user/saved-products',
  },
  removeProductFromProject: {
    api: (projectId: number, productId: number) =>
      `/private/projects/${projectId}/products/${productId}`,
  },
  getAllProjects: {
    api: '/private/projects/',
  },
};

export const SUBSCRIPTION_API = {
  createCheckoutSession: {
    api: '/private/stripe/create-checkout-session',
  },
  createPortalSession: {
    api: '/api/internal/checkout/create-portal-session',
  },
  generatePaymentUrl: {
    api: `/api/internal/checkout/generate-payment-url`,
  },
  createUnityCheckoutSession: {
    api: '/private/stripe/create-unity-checkout-session',
  },
  upgradeSubscription: {
    api: '/private/subscription/upgrade',
  },
  createCustomerPortalSession: {
    api: '/private/stripe/create-customer-portal-session',
  },
};

export const QUOTES_API = {
  createQuotes: {
    api: '/private/quotes',
  },
};

export const VENDOR_API = {
  inviteUser: {
    api: '/private/users/invite',
  },
  deleteUser: {
    api: (userId: string) => `/private/users/${userId}`,
  },
  getAssociatedVendorUsers: {
    api: '/private/users/associated',
  },
  updateLeadEmails: {
    api: '/private/vendors/update-lead-emails',
  },
  getVendorSettings: {
    api: '/private/vendors/email-settings',
  },
  uploadVendorFile: `/private/links/upload-file`,
};

export const VOICE_API = {
  getVoices: '/private/voice/voices',
  getFlags: '/private/voice/flags',
  getInitialVoiceAgentData: '/private/voice/voice-agent',
  getAmbientSounds: '/private/voice/ambient-sounds',
  createVoiceAgent: '/private/voice/create-voice-agent',
  updateVoiceAgent: (agent_id?: string) => `/private/voice/update-voice-agent/${agent_id}`,
  toggleVoiceAgent: (agent_id?: string) => `/private/voice/toggle-voice-agent/${agent_id}`,
  getCallHistory: '/private/voice/calls',
  createPhoneCall: '/private/voice/create-phone-call',
};

export const ANALYTICS_API = {
  getUserInsightsAnalytics: '/private/analytics/insights',
  getTrafficAnalytics: '/private/analytics/traffic',
  getCampaignPerformance: '/private/analytics/campaign-performance',
  getCampaignDetails: '/private/analytics/campaign-details',
  getCampaignFilterOptions: '/private/analytics/campaign-filter-options',
  getLatestVisitUniqueUsers: '/private/analytics/latest-visit-unique-users',
  getVisitUserDetails: '/private/analytics/visit-user-details',
  toggleIgnoreCustomer: '/private/analytics/toggle-ignore-customer',
  getSignalsFilterOptions: '/private/analytics/filter-options',
  namedAccountBulk: '/private/analytics/named-accounts/bulk',
  getNamedAccounts: '/private/analytics/named-accounts',
  deleteNamedAccounts: `/private/analytics/named-accounts/all`,
  excludedCustomerBulk: '/private/analytics/ignored-customers/bulk',
  getExcludedCustomers: '/private/analytics/ignored-customers',
  deleteExcludedCustomers: '/private/analytics/ignored-customers/all',
  getCampaignImpactData: '/private/analytics/campaign-impact',
  getCompanyInfluencingCampaigns: '/private/analytics/company-campaigns',
  getCampaignLandingPageStats: '/private/analytics/campaign-landing-stats',
  getVectorContacts: '/private/analytics/vector-contacts',
};

export const BOT_API = {
  getAllBotsData: (vendorId: number) => `/private/chatbot/${vendorId}`,
  getInternalAgent: '/private/chatbot/internal-agent',
  getInternalAgentSummary: (accessToken: string) =>
    `/private/chatbot/internal-agent-summary/${accessToken}`,
  upsertEntry: '/private/chatbot/training-data/replace',
  getEntries: (chatToken: string) => `/private/chatbot/training-data/${chatToken}`,
  createDefaultBot: '/api/internal/default-bot',
  improveAnswer: '/private/chatbot/training-data',
};

export const AUDIENCES_API = {
  getAllFacts: '/private/audience/facts',
  createAudience: '/private/audience',
  getAllAudiences: '/private/audience',
  deleteAudience: (id: number) => `/private/audience/${id}`,
  updateAudience: (id: number) => `/private/audience/${id}`,
  searchAudiences: '/private/audience/search',
  uploadCsvForAudience: '/private/audience/upload-csv',
  getAudienceCsvOrImagesDataUploaded: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number) =>
    `/private/audience/${type}/${list_id}`,
  deleteAllRowsByListId: (type: AUDIENCE_CSV_IMPORT_TYPE, list_id: number) =>
    `/private/audience/${type}/${list_id}`,
  uploadAudienceImages: '/private/audience/upload-images',
  getAudienceById: (id: number) => `/private/audience/${id}`,
  duplicateAudience: (audienceId: number) => `/private/audience/${audienceId}/duplicate`,
  runNow: (audienceId: number) => `/private/audience/${audienceId}/run-now`,
  testProcessing: '/private/audience/test-processing',
  getProcessedRecords: '/private/audience/processed-records',
  getCountries: '/private/audience/countries',
  previewHubSpotContacts: '/private/integration/hubspot/preview',
  // HubSpot Lists
  getHubSpotLists: '/private/integration/hubspot/lists',
  getHubSpotListContacts: (listId: number | string) =>
    `/private/integration/hubspot/lists/${listId}/contacts`,
  importHubSpotListContacts: (listId: number | string) =>
    `/private/integration/hubspot/lists/${listId}/import`,
  getAudienceCountries: '/private/audience/countries',
  getAvailableWorkflows: '/private/plays/copy/available-workflows',
  copyWorkflow: '/private/plays/copy/actions',
};

export const PLAYS_API = {
  getAllPlays: '/private/plays',
  getPlayListings: '/private/plays/listings',
  storeAudiencePlay: '/private/plays',
  getPlayDetails: (id: number) => `/private/plays/details/${id}`,
  removeAudiencePlay: (id: number) => `/private/plays/${id}`,
  patchPlayNode: (nodeId: number) => `/private/plays/nodes/${nodeId}`,
  removePlay: (audienceId: number) => `/private/plays/audiences/${audienceId}`,
  checkPlayName: (playName: string) => `/private/audience/check-name/${playName}`,
  createAIAgent: '/private/plays/agents',
  getAIAgents: '/private/plays/agents',
  updateAIAgent: (id: number) => `/private/plays/agents/${id}`,
  getAIAgentById: (id: number) => `/private/plays/agents/${id}`,
  publishPlay: `/private/plays/publish`,
  getPlaysPhoneNumber: '/private/plays/phone-numbers',
  addPlaysPhoneNumber: '/private/plays/phone-numbers',
  getPlayResults: (id: number) => `/private/plays/results/${id}`,
  getPlayExecution: (id: number) => `/private/plays/executions/${id}`,
  getEmailDynamicVariables: '/private/plays/dynamic-variables',
  createEmailTemplate: '/private/plays/email-templates',
  updateEmailTemplate: (id: number) => `/private/plays/email-templates/${id}`,
  getEmailTemplates: '/private/plays/email-templates',
  uploadPlayImage: '/private/plays/upload-image',
  getChatChannels: (platform: string) => `/private/plays/chat-channels/${platform}`,
  deleteAIAgent: (id: number) => `/private/plays/agents/${id}`,
};

export const PERSONA_API = {
  generateTargetAudience: '/private/persona/generate-target-audience',
  createPersona: '/private/persona',
  getAllPersonas: '/private/persona/listings',
  deletePersona: (id?: number) => `/private/persona/${id}`,
  getPersonaDetails: (id?: number) => `/private/persona/details/${id}`,
  updatePersona: (id?: number) => `/private/persona/${id}`,
};

const INTEGRATIONS_API = {
  getSalesforceFields: '/private/integration/fetch-salesforce-fields',
  getHubspotFields: '/private/integration/fetch-hubspot-fields',
  receiveCalendlyScript: '/private/integration/receive-calendly-script',
  getCalendarTimeSlots: '/public/integration/time-slots',
  getGoogleCalendarList: '/private/integration/google-calendars',
  selectGoogleCalendar: '/private/integration/select-google-calendar',
  addSalesForce: '/private/integration/initiate-salesforce-auth',
  addHubSpot: '/private/integration/initiate-hubspot-auth',
  addApollo: '/private/integration/initiate-apollo-auth',
  getPublicCalendarData: (slug: string) => `/public/integration/meeting/book/me/${slug}`,
  getIntegrationsByChatToken: (chatToken: string) =>
    `/private/integration/get-integrations-by-chat-token/${chatToken}`,
  bookMeeting: '/public/integration/book-meeting',
  getEmailIntegrations: '/private/integration/list-email-integrations',
  saveHubSpotMappings: '/private/integration/save-hubspot-field-mappings',
  getHubSpotFieldMappings: '/private/integration/get-hubspot-field-mappings',
  saveHubSpotMappingsEnhanced: '/private/integration/save-hubspot-field-mappings-enhanced',
  getHubSpotFieldMappingsEnhanced: '/private/integration/get-hubspot-field-mappings-enhanced',
  saveSalesforceMappings: '/private/integration/save-salesforce-field-mappings',
  getSalesforceFieldMappings: '/private/integration/get-salesforce-field-mappings',
  addLinkedin: '/private/integration/initiate-linkedin-auth',
  addGoogleAds: '/private/integration/initiate-google-ads-auth',
  getWebhook: (platform: string) => `/private/webhooks/${platform}`,
  generateWebhook: (platform: string) => `/private/webhooks/${platform}/generate`,
  getAllCalendarsByUser: '/private/integration/get-all-calendars-by-user',
  getAllEmailAccountsByUser: '/private/integration/get-all-email-accounts-by-user',
  addNotion: '/private/integration/initiate-notion-auth',
  getNotionDatabasesAndPages: '/private/integration/get-notion-databases-and-pages',
  saveSelectedNotionPages: '/private/dam/notion/pages',
  getSelectedNotionPages: '/private/dam/notion/pages',
  addBynder: '/private/dam/bynder/auth',
  getSelectedNotionPagesWithStatus: '/private/dam/notion/pages/status',
  trainNotionPages: '/private/dam/notion/train',
  getBynderCollections: '/private/dam/bynder/collections',
  saveSelectedBynderCollections: '/private/dam/bynder/collections',
  getSelectedBynderCollections: '/private/dam/bynder/selected-collections',
  getSelectedBynderCollectionsWithStatus: '/private/dam/bynder/collections/status',
  trainBynderCollections: '/private/dam/bynder/train',
  getBynderAssets: '/private/dam/bynder/assets',
  saveSelectedBynderAssets: '/private/dam/bynder/assets',
  getSelectedBynderAssets: '/private/dam/bynder/selected-assets',
  getBynderAssetsWithStatus: '/private/dam/bynder/assets/status',
  addHeyGen: '/private/integration/initiate-heygen-auth',
  addGoogleSheets: '/private/integration/initiate-google-sheets-auth',
  addGoogleDrive: '/private/integration/initiate-google-drive-auth',
  addVector: '/private/integration/initiate-vector-auth',
  getGoogleSheetsAuthId: '/private/integration/auth-id',
};

const WORKFLOW_API = {
  getWorkflows: '/private/workflows',
  queryWorkflow: '/private/workflows/stream',
};

const CONVERSATIONS_API = {
  createConversation: '/private/conversations',
  streamConversation: (id: string) => `/private/conversations/${id}/stream`,
  getConversation: (id: string) => `/private/conversations/${id}`,
};

const FEEDBACK_API = {
  feedbacks: '/private/feedback/requests',
  updateFeedback: (id: string) => `/private/feedback/requests/${id}`,
  voteFeedback: (id: string) => `/private/feedback/requests/${id}/vote`,
  categories: '/private/feedback/categories',
};

const TOKENS_API = {
  createApiToken: '/private/tokens',
};

const WORKSPACE_API = {
  // Core workspace operations
  listWorkspaces: '/private/workspaces',
  getActiveWorkspace: '/private/workspaces/active',
  createWorkspace: '/private/workspaces',
  getWorkspace: (id: number) => `/private/workspaces/${id}`,
  updateWorkspace: (id: number) => `/private/workspaces/${id}`,
  deleteWorkspace: (id: number) => `/private/workspaces/${id}`,

  // Workspace switching
  switchWorkspace: '/private/workspaces/switch',
  joinWorkspace: '/private/workspaces/join',

  // Member management
  getWorkspaceMembers: (id: number) => `/private/workspaces/${id}/members`,
  updateMemberPermissions: (id: number, memberId: string) =>
    `/private/workspaces/${id}/members/${memberId}`,
  removeMember: (id: number, memberId: string) => `/private/workspaces/${id}/members/${memberId}`,
  requestCreditAccess: '/private/workspaces/request-credit-access',

  // Admin management (Multi-Admin feature)
  promoteToAdmin: (workspaceId: number, userId: string) =>
    `/private/workspaces/${workspaceId}/admins/${userId}`,
  demoteFromAdmin: (workspaceId: number, userId: string) =>
    `/private/workspaces/${workspaceId}/admins/${userId}`,

  // Join code management
  generateJoinCode: (workspaceId: number) => `/private/workspaces/${workspaceId}/generate-join-code`,

  // Credit management
  getWorkspaceCredits: '/private/workspaces/credits',
  getWorkspaceCreditsById: (id: number) => `/private/workspaces/${id}/credits`,
  planCreditConsumption: '/private/workspaces/credits/plan',
  consumeCredits: '/private/workspaces/credits/consume',
  addCredits: '/private/workspaces/credits/add',
  getCreditHistory: '/private/workspaces/credits/history',
  getCreditAnalytics: '/private/workspaces/credits/analytics',
  getCreditTransactions: '/private/workspaces/credits/transactions',
  purchaseCredits: '/private/workspaces/credits/purchase',

  // Invitations
  sendInvitation: '/private/workspaces/invite',
  sendInvitationToWorkspace: (id: number) => `/private/workspaces/${id}/invite`,
  getInvitations: '/private/workspaces/invitations',
  getWorkspaceInvitations: (id: number) => `/private/workspaces/${id}/invitations`,
  revokeInvitation: (invitationId: number) => `/private/workspaces/invitations/${invitationId}`,
  resendInvitation: (invitationId: number) =>
    `/private/workspaces/invitations/${invitationId}/resend`,
  acceptInvitation: (token: string) => `/private/workspaces/invite/${token}/accept`,

  // Company research and workflow templates
  triggerCompanyResearch: (id: number) => `/private/workspaces/${id}/research`,
  getCompanyResearch: (id: number) => `/private/workspaces/${id}/research`,
  generateWorkflowTemplates: (id: number) => `/private/workspaces/${id}/workflow-templates`,
  getWorkflowTemplates: (id: number) => `/private/workspaces/${id}/workflow-templates`,
};

export const TEMPLATES_API = {
  CREATE: '/private/templates',
  UPDATE: (templateId: string) => `/private/templates/${templateId}`,
  DELETE: (templateId: string) => `/private/templates/${templateId}`,
  GET: (templateId: string) => `/private/templates/${templateId}`,
  LIST: '/private/templates',
  RENDER: (templateId: string) => `/private/templates/${templateId}/render`,
};

export const API_ENDPOINTS = {
  LOGIN: '/api/internal/auth/login',
  SIGNUP: '/api/internal/auth/sign-up',
  LOGOUT: '/api/internal/auth/logout',
  CREATE_USER: '/private/users/',
  GET_LEADS: '/private/vendors/vendor-leads',
  getAskIotUserDetails: '/private/users/api/',
  createAskIotUser: '/private/users/',
  createUnityUser: '/public/users/create-unity-user',
  createStandaloneUser: '/public/users/create-standalone-user',

  updateUserCalendarSettings: '/private/users/calendar-settings',
  REQUEST_EARLY_ACCESS: '/public/users/request-early-access',
  ...IOT_GPT_API,
  ...PRODUCT_API,
  ...QUOTES_API,
  ...SUBSCRIPTION_API,
  ...PROJECTS_API,
  ...VENDOR_API,
  ...ANALYTICS_API,
  createUserSuggestedIdea: '/private/content/create-suggested-idea',
  updateLinkOptions: '/private/links/link-options-bulk',
  getLinkOptions: (linkId: string | number) => `/private/links/link-options/${linkId}`,
  uploadLinkFile: '/private/links/upload-file',
  getVendorId: '/private/chatbot/vendor-id',
  getDashboardScore: '/private/chatbot/get-dashboard-stats',
  getHelpSection: (token: any) => `/public/chatbot/help-section/${token}`,
  getBotBlog: (token: any) => `/public/chatbot/highlighted-page/${token}`,
  getBotWebinar: (token: any) => `/public/chatbot/webinar/${token}`,
  getDemoDetails: (token: any) => `/public/chatbot/demo-details/${token}`,
  getBotData: (token: any) => `/public/chatbot/get/${token}`,
  getLLMSetting: (token: any) => `/private/chatbot/advanced-settings/${token}`,
  listPersonas: (token: any) => `/private/chatbot/personas/${token}`,
  getLinks: `/private/links/list-links`,
  getTrainedData: `/private/training/get`,
  getVideoLinks: (token: any) => `/private/links/list-links?chat_token=${token}&type=video`,
  getQA: (token: any) => `/private/chatbot/faq-content/${token}`,
  getThreadDetails: (token: any) => `private/chatbot/threads/vendor?chat_token=${token}`,
  removeTrainedData: (file_id: number) => `/private/chatbot/delete-file/${file_id}`,
  generateOutline: '/private/content/content-interaction',
  generateImages: '/private/content/generate-images',
  enhanceArticle: '/private/content/enhance-article',
  getArchive: '/private/content/markInteractionInactive',
  generateCloudFlareUploadVideoUrl: '/private/links/generate-upload-url',
  validateEmail: '/public/users/validate-email',
  convertDiscoveredLink: (linkId: number) => `/private/links/convert-discovered/${linkId}`,
  ...WORKFLOW_API,
  ...CONVERSATIONS_API,
  ...FEEDBACK_API,
  ...INTEGRATIONS_API,
  ...VOICE_API,
  ...BOT_API,
  ...AUDIENCES_API,
  ...PLAYS_API,
  ...PERSONA_API,
  TEMPLATES: TEMPLATES_API,
  ...TOKENS_API,
  ...WORKSPACE_API,
  HEYGEN_AVATARS: '/private/integration/heygen/avatars',
  HEYGEN_VOICES: '/private/integration/heygen/voices',
  HEYGEN_AVATAR_GROUPS: '/private/integration/heygen/avatar-groups',
  HEYGEN_AVATARS_IN_GROUP: '/private/integration/heygen/avatar-group',
  HEYGEN_UPLOAD_ASSET: '/private/integration/heygen/assets/upload',
};
